"use client"

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode, useRef } from "react"
import { usePathname, useRouter } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { pingActive } from "@/lib/activity/heartbeat"
import type { AuthChangeEvent, Session } from "@supabase/supabase-js"
import { resolveClientAvatar } from "@/lib/client-avatar"
import { withTimeout, AUTH_CALL_TIMEOUT_MS } from "@/lib/supabase/with-timeout"

export type UserRole = "barber" | "owner"

interface UserProfile {
  id: string
  first_name?: string
  full_name?: string
  email?: string
  role: UserRole
  [key: string]: any
}

interface UserRoleContextType {
  role: UserRole
  setRole: (role: UserRole) => void
  actualUserType: UserRole | null
  isOwner: boolean
  /** True when the logged-in user is a CLIENT (has a clients row, no pro role).
   *  Clients are walled off from the barber/owner role system. */
  isClient: boolean
  /** Display name + avatar for a CLIENT (clients have no profiles row). Lets
   *  the nav account row read "Enzo Ferrari" with their photo instead of a
   *  generic "My account". Null for pro accounts / logged-out. */
  clientName: string | null
  clientAvatarUrl: string | null
  refetch: () => Promise<void>
  isLoading: boolean
  userId: string | null
  roleVerified: boolean
  roleError: string | null
  profile: UserProfile | null
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined)

function getInitialRole(): UserRole {
  if (typeof window === "undefined") return "barber"
  const stored = localStorage.getItem("user-role")
  if (stored === "owner" || stored === "barber") return stored as UserRole
  return "barber"
}

export function UserRoleProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const [role, setRoleState] = useState<UserRole>(getInitialRole)
  const [actualUserType, setActualUserType] = useState<UserRole | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [roleVerified, setRoleVerified] = useState(false)
  const [roleError, setRoleError] = useState<string | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isClient, setIsClient] = useState(false)
  const [clientName, setClientName] = useState<string | null>(null)
  const [clientAvatarUrl, setClientAvatarUrl] = useState<string | null>(null)
  const fetchAttemptRef = useRef(0)
  // Who the last successful fetch loaded. Read by the auth listener, which
  // closes over the first render and so cannot see the userId state.
  const loadedUserIdRef = useRef<string | null>(null)
  const maxRetries = 3

  // Wipe every trace of a signed-in identity. Used both by the SIGNED_OUT
  // handler and by the "no user" branch of fetchUserType — the two paths must
  // leave exactly the same state, or consumers disagree about who's logged in.
  const clearIdentity = useCallback(() => {
    setProfile(null)
    setActualUserType(null)
    setUserId(null)
    loadedUserIdRef.current = null
    setIsClient(false)
    setClientName(null)
    setClientAvatarUrl(null)
    setRoleVerified(false)
  }, [])

  const fetchUserType = useCallback(async (isRetry = false) => {
    if (typeof window === "undefined") {
      setIsLoading(false)
      return
    }

    if (!isRetry) {
      fetchAttemptRef.current = 0
    }
    fetchAttemptRef.current++

    try {
      setIsLoading(true)
      setRoleError(null)
      const supabase = createClient()

      const {
        data: { user },
        error: userError,
      } = await withTimeout(
        supabase.auth.getUser(),
        AUTH_CALL_TIMEOUT_MS,
        () => ({ data: { user: null }, error: { message: "auth_timeout" } }) as any,
      )

      if (userError || !user) {
        console.log("[v0] Auth check failed:", userError?.message || "No user")

        // A clean "no user" answer is definitive — the visitor is signed out.
        // Only a transport error (timeout/network) is worth retrying; retrying
        // a logged-out visitor just holds isLoading true and delays the public
        // Sign Up CTA for no reason.
        const isTransient = Boolean(userError)

        if (isTransient && fetchAttemptRef.current < maxRetries) {
          const delay = Math.pow(2, fetchAttemptRef.current) * 500
          console.log(`[v0] Retrying auth in ${delay}ms (attempt ${fetchAttemptRef.current}/${maxRetries})`)
          setTimeout(() => fetchUserType(true), delay)
          return
        }

        // Signed out means signed out. This used to return without clearing
        // identity, so a session that lapsed WITHOUT firing SIGNED_OUT (expiry,
        // cookie cleared, sign-out in another tab) left userId populated —
        // and every consumer kept rendering authenticated chrome. That's how a
        // logged-out visitor ended up seeing a notification bell, with a stale
        // unread count, on the public landing page.
        clearIdentity()
        setIsLoading(false)
        return
      }

      console.log("[v0] User authenticated:", user.id)
      setUserId(user.id)
      loadedUserIdRef.current = user.id

      // Client-mode display identity (avatar + name) is sourced from auth
      // metadata so the floating nav and the client account page — which BOTH
      // read user_metadata — always show the same photo, even for a pro
      // browsing in Client mode (whose barber profile photo can differ).
      const meta = (user.user_metadata ?? {}) as Record<string, any>
      setClientAvatarUrl((meta.avatar_url as string) || null)
      setClientName(meta.full_name || meta.first_name || null)

      // Fetch user profile with rate limit handling
      let profileData = null
      let profileError = null
      
      try {
        // maybeSingle (not single): a CLIENT has no profiles row, and single()
        // would return an error that short-circuits before the client-detection
        // below ever runs. maybeSingle returns { data: null } with no error so
        // the no-role path (which sets isClient) is reached.
        // Cast to a loose shape: wrapping the query builder in withTimeout
        // otherwise collapses the inferred row type (the fallback's `data:
        // null` unions with the real row) and every downstream profileData.*
        // access becomes a `never` error. The original un-wrapped call was
        // effectively `any` here too.
        const result = (await withTimeout(
          supabase.from("my_profile").select("*").maybeSingle(),
          AUTH_CALL_TIMEOUT_MS,
          () => ({ data: null, error: { message: "profile_timeout", code: "timeout" } }),
        )) as { data: any; error: any }

        profileData = result.data
        profileError = result.error
      } catch (fetchError: any) {
        // Handle rate limit or network errors
        console.error("[v0] Profile fetch error:", fetchError)
        
        // Retry with backoff for rate limits
        if (fetchAttemptRef.current < maxRetries) {
          const delay = Math.pow(2, fetchAttemptRef.current) * 1000
          console.log(`[v0] Retrying profile fetch in ${delay}ms (attempt ${fetchAttemptRef.current}/${maxRetries})`)
          setTimeout(() => fetchUserType(true), delay)
          return
        }
        
        setRoleError("Service temporarily unavailable")
        setRoleVerified(false)
        setIsLoading(false)
        return
      }

      if (profileError) {
        console.error("[v0] Profile fetch error:", profileError.message)
        
        // Retry for rate limit errors
        if (profileError.message?.includes('Too Many') || profileError.code === '429') {
          if (fetchAttemptRef.current < maxRetries) {
            const delay = Math.pow(2, fetchAttemptRef.current) * 1000
            console.log(`[v0] Rate limited, retrying in ${delay}ms`)
            setTimeout(() => fetchUserType(true), delay)
            return
          }
        }
        
        setRoleError(profileError.message)
        setRoleVerified(false)
        setIsLoading(false)
        return
      }

      if (!profileData?.role) {
        // No pro role → this may be a CLIENT (walled off from profiles). Check
        // the clients table (RLS lets a client read their own row).
        const metaIsClient = (meta.role as string) === "client"
        try {
          const { data: clientRow, error: clientErr } = await supabase
            .from("clients")
            .select("id, name")
            .eq("auth_user_id", user.id)
            .maybeSingle()
          // If the clients read is unavailable (e.g. an RLS error), don't let
          // that silently strip the client identity — fall back to the auth
          // metadata role so the nav account row + client surfaces still show.
          setIsClient(!!clientRow || (!!clientErr && metaIsClient))
          // Prefer the clients-row name over the metadata fallback set above.
          if (clientRow?.name?.trim()) setClientName(clientRow.name.trim())
        } catch {
          setIsClient(metaIsClient)
        }
        console.warn("[v0] User profile missing role")
        setRoleVerified(false)
        setIsLoading(false)
        return
      }
      // Has a pro role → definitively not a client. Their Client-mode avatar
      // resolves from profiles.avatar_url (the canonical store — the pro
      // upload flow deletes old storage files, so the metadata copy can be a
      // dead link). Keep clientName from metadata for the client surfaces.
      setIsClient(false)
      setClientAvatarUrl(
        resolveClientAvatar({
          hasProProfile: true,
          profileAvatarUrl: (profileData as { avatar_url?: string | null }).avatar_url,
          metadataAvatarUrl: (meta.avatar_url as string) || null,
        }),
      )

      console.log("[v0] Profile loaded:", { id: profileData.id, role: profileData.role })

      setProfile(profileData as UserProfile)
      const userRole = profileData.role as UserRole
      setActualUserType(userRole)
      setRoleVerified(true)

      // Always respect the stored role preference in localStorage
      // This allows any user to switch between barber and owner modes
      const storedRole = localStorage.getItem("user-role") as UserRole | null

      if (storedRole === "barber" || storedRole === "owner") {
        // Use the stored preference
        setRoleState(storedRole)
      } else {
        // No stored preference - use the actual user type as default
        setRoleState(userRole)
        localStorage.setItem("user-role", userRole)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error"
      console.error("[v0] Unexpected error:", errorMessage)
      setRoleError(errorMessage)
      setRoleVerified(false)
    } finally {
      setIsLoading(false)
    }
  }, [clearIdentity])

  useEffect(() => {
    setMounted(true)
    fetchUserType()
  }, [fetchUserType])

  // Activity heartbeat for the hiring talent ranking (lib/activity/heartbeat).
  // On sign-in and whenever the tab comes back into view.
  useEffect(() => {
    if (!userId) return
    const supabase = createClient()
    pingActive(supabase, userId)
    const onVisible = () => {
      if (document.visibilityState === "visible") pingActive(supabase, userId)
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => document.removeEventListener("visibilitychange", onVisible)
  }, [userId])

  useEffect(() => {
    const supabase = createClient()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      console.log("[v0] Auth state changed:", event)

      // Only refetch on a genuine sign-in. TOKEN_REFRESHED fires on Supabase's
      // periodic background token rotation — the user identity is unchanged, so
      // re-running the full profile fetch there just flips `isLoading` back to
      // true (flashing the whole dashboard to the loading screen) and hammers
      // the DB on a timer. INITIAL_SESSION is already covered by the mount
      // effect's fetchUserType() call.
      //
      // supabase-js also fires SIGNED_IN for a session it merely RECOVERED —
      // on page load and every time the tab regains focus — not only for a
      // real sign-in. Refetching then flipped isLoading on a user we already
      // had, so the dashboard dropped to its loader and remounted, which is
      // the flash of the "Finish your profile" card on load. Same user, no
      // refetch; a different user (or none loaded yet) still refetches.
      if (event === "SIGNED_IN") {
        if (session?.user?.id && session.user.id === loadedUserIdRef.current) return
        fetchAttemptRef.current = 0
        fetchUserType()
      } else if (event === "SIGNED_OUT") {
        clearIdentity()
        setRoleState("barber")
        localStorage.removeItem("user-role")
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [fetchUserType, clearIdentity])

  useEffect(() => {
    const handleRoleChanged = (event: CustomEvent<{ role: UserRole }>) => {
      setRoleState(event.detail.role)
    }

    window.addEventListener("role-changed", handleRoleChanged as EventListener)
    return () => {
      window.removeEventListener("role-changed", handleRoleChanged as EventListener)
    }
  }, [])

  // Both account pages dispatch "profile-updated" after an avatar upload —
  // update the nav's avatar immediately instead of waiting for a full refetch.
  useEffect(() => {
    const handleProfileUpdated = (event: CustomEvent<{ avatarUrl?: string | null }>) => {
      if (event.detail?.avatarUrl) {
        setClientAvatarUrl(event.detail.avatarUrl)
        setProfile((p) => (p ? { ...p, avatar_url: event.detail.avatarUrl } : p))
      }
    }
    window.addEventListener("profile-updated", handleProfileUpdated as EventListener)
    return () => {
      window.removeEventListener("profile-updated", handleProfileUpdated as EventListener)
    }
  }, [])

  // The dashboard the user is standing on IS their mode. A dual-role user
  // browsing /dashboard/barber then tapping Browse was snapping to owner on
  // /listings, because off-dashboard surfaces read the stored preference and
  // nothing had updated it. Passive sync: standing on a pro dashboard records
  // that mode (state + localStorage + role-changed event), with no navigation.
  useEffect(() => {
    if (!userId || isClient) return
    const urlMode: UserRole | null = pathname?.startsWith("/dashboard/owner")
      ? "owner"
      : pathname?.startsWith("/dashboard/barber")
        ? "barber"
        : null
    if (urlMode && urlMode !== role) {
      setRoleState(urlMode)
      localStorage.setItem("user-role", urlMode)
      window.dispatchEvent(new CustomEvent("role-changed", { detail: { role: urlMode } }))
    }
  }, [pathname, userId, isClient, role])

  const setRole = useCallback(
    (newRole: UserRole) => {
      if (!mounted || typeof window === "undefined") return

      // Allow ANY user to switch to ANY role - no restrictions
      console.log("[v0] Switching role to:", newRole)
      setRoleState(newRole)
      localStorage.setItem("user-role", newRole)
      window.dispatchEvent(new CustomEvent("role-changed", { detail: { role: newRole } }))
      
      const targetPath = newRole === "owner" ? "/dashboard/owner" : "/dashboard/barber"
      router.push(targetPath)
    },
    [mounted, router],
  )

  const isOwner = actualUserType === "owner"

  const refetch = useCallback(async () => {
    fetchAttemptRef.current = 0
    await fetchUserType()
  }, [fetchUserType])

  return (
    <UserRoleContext.Provider
      value={{ role, setRole, actualUserType, isOwner, isClient, clientName, clientAvatarUrl, refetch, isLoading, userId, roleVerified, roleError, profile }}
    >
      {children}
    </UserRoleContext.Provider>
  )
}

export function useUserRole(): UserRoleContextType {
  const context = useContext(UserRoleContext)
  if (!context) {
    if (typeof window !== "undefined") {
      return {
        role: "barber",
        setRole: () => {},
        actualUserType: null,
        isOwner: false,
        isClient: false,
        clientName: null,
        clientAvatarUrl: null,
        refetch: async () => {},
        isLoading: false,
        userId: null,
        roleVerified: false,
        roleError: null,
        profile: null,
      }
    }
    throw new Error("useUserRole must be used within a UserRoleProvider")
  }
  return context
}

export const useProfile = useUserRole
